import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { LOAN_STATUS } from '../../../../screens/Loans/constants';
import { LOANS_ALL } from '../../../../screens/Loans/queries';
import ModalBodyLayout from '../../../../screens/shared/ModalBodyLayout';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import useIsUsUser from '../../../../utilities/sharedHooks/usUser';
import {
  useUserAllowStakingFeature,
  useUserPersonalInfo,
} from '../../../../utilities/sharedHooks/user';
import { USER_REGION_SETTINGS_QUERY_KEY } from '../../../../utilities/sharedQueries';

const Services = ({ hide }) => {
  const { t: translate } = useTranslation();
  const isUSUser = useIsUsUser();
  const { path } = useRouteMatch();
  const history = useHistory();

  const [allowOTCFeature] = useSetting('app_features', 'feature_allow_otc');
  const [allowOxygenYieldFeature] = useSetting('app_features', 'feature_allow_oxygen_yield');

  const isAllowStakingFeature = useUserAllowStakingFeature();

  const { allowOxygenYield } = useUserPersonalInfo();

  const { data: userRegionSettings } = useQuery(USER_REGION_SETTINGS_QUERY_KEY);

  const oxygenYieldDisabled = useMemo(
    () => !allowOxygenYieldFeature || !allowOxygenYield || !userRegionSettings?.can_access_yield,
    [allowOxygenYieldFeature, allowOxygenYield, userRegionSettings]
  );

  const [loansEnabled, isLoadingSettings] = useSetting('app_features', 'feature_allow_loans');
  const [newLoanEnabled, isLoadingNewLoan] = useSetting('app_features', 'feature_allow_new_loans');
  const { data: loansData, isLoading: isLoadingLoans } = useQuery(LOANS_ALL);

  const activeLoans = useMemo(() => {
    if (!isLoadingLoans && loansData) {
      return loansData?.filter((loan) => loan.status === LOAN_STATUS.ACTIVE);
    }

    return [];
  }, [isLoadingLoans, loansData]);

  const loansTabDisabled = useMemo(() => {
    let disabled = true;

    if (!isLoadingSettings && loansEnabled) {
      if (!isLoadingNewLoan && !newLoanEnabled) {
        if (isLoadingLoans) {
          disabled = false;
        } else if (activeLoans?.length > 0) {
          disabled = false;
        } else {
          disabled = true;
        }
      } else if (!isLoadingNewLoan && newLoanEnabled) {
        disabled = false;
      }
    }

    return disabled;
  }, [
    activeLoans,
    isLoadingLoans,
    isLoadingNewLoan,
    isLoadingSettings,
    loansEnabled,
    newLoanEnabled,
  ]);

  const handleOnClickService = (route) => {
    history.push(`${path}/${route}`);
    hide();
  };

  const servicesList = useMemo(
    () => [
      {
        label: translate('oxygen_yield_tab_label'),
        disabled: oxygenYieldDisabled,
        icon: 'icon-document-other',
        route: 'oxygen_yield',
      },
      {
        label: translate(`${isUSUser ? 'loans_tab_label_US' : 'loans_tab_label'}`),
        disabled: loansTabDisabled,
        icon: 'icon-graph-navigation',
        route: 'loans',
      },
      {
        label: translate('OTC_tab_label'),
        disabled: !allowOTCFeature,
        icon: 'icon-otc1',
        route: 'otc',
      },
      {
        label: translate('staking_tab_label'),
        disabled: !isAllowStakingFeature,
        icon: 'icon-staking',
        route: 'staking',
        iconStyle: { transform: 'scale(0.75)' },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isUSUser, loansTabDisabled, allowOTCFeature, oxygenYieldDisabled, isAllowStakingFeature]
  );

  const renderButton = ({ disabled, route, label, icon, key, iconStyle }) => (
    <div
      key={key}
      role="button"
      tabIndex="-1"
      onClick={() => handleOnClickService(route)}
      onKeyDown={() => handleOnClickService(route)}
      className={`bg-light cursor-pointer hover-container rounded-3 p-4 d-flex flex-row justify-content-between align-items-center mb-3 ${
        disabled ? 'd-none' : ''
      }`}>
      <div className="d-flex flex-row gap-2">
        <i
          style={{ ...iconStyle, width: '1rem' }}
          className={`icon ${icon} text-white text-f4 cursor-pointer me-2`}
        />
        <p>{label}</p>
      </div>
      <i
        style={{ width: '1rem' }}
        className="icon icon-arrow-next-navigation text-primary text-f4 cursor-pointer"
        role="button"
        aria-label={label}
        tabIndex={0}
        onKeyDown={() => {}}
        onClick={() => {}}
      />
    </div>
  );

  return (
    <ModalBodyLayout withBack={false} isScrollable title={translate('services_tab_label')}>
      <div className="mt-4">
        {servicesList.map((item) =>
          renderButton({
            disabled: item.disabled,
            label: item.label,
            icon: item.icon,
            iconStyle: item.iconStyle || {},
            route: item.route,
            key: item.label,
          })
        )}
      </div>
    </ModalBodyLayout>
  );
};

Services.propTypes = {
  hide: PropTypes.func.isRequired,
};

export default Services;
